/**
 * Pure frame maths (no DOM) — shared by the still gate (media/imageQuality.ts) and the live clip
 * sampler (capture/clipQuality.ts). Extracted from vCIP_droom_4.0/src/media/imageQuality.ts so the
 * numbers can be pinned by tests without a canvas.
 */

export interface FrameMetrics {
  readonly meanLuma: number;
  readonly laplacianVar: number;
  /** Fraction (0..1) of pixels at or above CLIPPED_LUMA — blown-out specular highlights, the glare signature
   *  on a laminated card. A well-exposed white card sits well below the clip point; a lamp reflection does not. */
  readonly clippedRatio: number;
}

/** Luma at which a pixel counts as clipped (blown out). */
export const CLIPPED_LUMA = 250;

/**
 * Mean luma (Rec. 601), the variance of the 4-neighbour Laplacian over interior pixels — the
 * classic focus measure: soft (defocused) edges → low variance — and the clipped-highlight ratio.
 * @param data RGBA bytes, row-major, `w*h*4` long.
 */
export function frameMetrics(data: ArrayLike<number>, w: number, h: number): FrameMetrics {
  const n = w * h;
  if (n === 0) return { meanLuma: 0, laplacianVar: 0, clippedRatio: 0 };
  const luma = new Float32Array(n);
  let sum = 0;
  let clipped = 0;
  for (let i = 0; i < n; i += 1) {
    const o = i * 4;
    const y = 0.299 * (data[o] ?? 0) + 0.587 * (data[o + 1] ?? 0) + 0.114 * (data[o + 2] ?? 0);
    luma[i] = y;
    sum += y;
    if (y >= CLIPPED_LUMA) clipped += 1;
  }
  const meanLuma = sum / n;
  const clippedRatio = clipped / n;

  let lapSum = 0;
  let lapSqSum = 0;
  let count = 0;
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      const lap =
        (luma[i - 1] ?? 0) + (luma[i + 1] ?? 0) + (luma[i - w] ?? 0) + (luma[i + w] ?? 0) -
        4 * (luma[i] ?? 0);
      lapSum += lap;
      lapSqSum += lap * lap;
      count += 1;
    }
  }
  const lapMean = count > 0 ? lapSum / count : 0;
  const laplacianVar = count > 0 ? lapSqSum / count - lapMean * lapMean : 0;
  return { meanLuma, laplacianVar, clippedRatio };
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}
